import { useEffect, useMemo, useState } from "react";
import api, { money, fmtDate, fmtHours, API_BASE } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  FileDown, Filter, ClipboardList, Clock, Receipt, Barcode, TrendingUp, ChevronLeft, ChevronRight, X,
} from "lucide-react";

// Redesenho da tela de Relatórios (delta 2026-09-16) — só visual/UX +
// exportação PDF; dados, regras e endpoints de relatório permanecem
// exatamente os mesmos (GET /reports/services|hours|invoicing|boletos|
// productivity, sem nenhuma alteração). Excel deixa de aparecer na
// interface (item 2 do pedido) — doExportXlsx/o endpoint continuam
// existindo no backend, sem uso nesta tela.
const PAGE_SIZE = 10;

// Mesmas traduções usadas em Fichas.jsx/Dashboard.jsx — usuário nunca deve ver o
// slug técnico do status (ex.: "rejeitada_supervisor") em nenhuma tela do sistema.
const statusPt = (s) => ({
  em_andamento: "Em andamento", concluida: "Concluída",
  rejeitada_supervisor: "Rejeitada pelo supervisor", rejeitada_cliente: "Rejeitada pelo cliente",
  aguardando_aprovacao: "Aguardando aprovação",
  aguardando_assinatura: "Aguardando assinatura",
  aguardando_faturamento: "Aguardando faturamento", faturada: "Faturada", cancelada: "Cancelada",
}[s] || s);
const statusColor = (s) => ({
  em_andamento: "bg-blue-100 text-blue-800", concluida: "bg-emerald-100 text-emerald-800",
  rejeitada_supervisor: "bg-red-100 text-red-800", rejeitada_cliente: "bg-red-100 text-red-800",
  aguardando_aprovacao: "bg-amber-100 text-amber-800", aguardando_assinatura: "bg-orange-100 text-orange-800",
  aguardando_faturamento: "bg-indigo-100 text-indigo-800", faturada: "bg-emerald-100 text-emerald-800",
  cancelada: "bg-slate-200 text-slate-700",
}[s] || "bg-slate-100 text-slate-700");
// Mesmos rótulos/cores já usados em Faturamento.jsx para faturamento/boletos
// — só reaproveitados aqui como badge, nunca um status novo/inventado.
const invoiceStatusPt = (s) => ({ em_aberto: "Em aberto", parcialmente_pago: "Parcialmente pago", pago: "Pago", cancelado: "Cancelado" }[s] || s);
const invoiceStatusColor = (s) => ({
  em_aberto: "bg-blue-100 text-blue-800", parcialmente_pago: "bg-amber-100 text-amber-800",
  pago: "bg-emerald-100 text-emerald-800", cancelado: "bg-slate-200 text-slate-700",
}[s] || "bg-slate-100 text-slate-700");
const boletoStatusPt = (s) => ({ em_aberto: "Em aberto", pago: "Pago", vencido: "Vencido", cancelado: "Cancelado" }[s] || s);
const boletoStatusColor = (s) => ({
  em_aberto: "bg-blue-100 text-blue-800", pago: "bg-emerald-100 text-emerald-800",
  vencido: "bg-red-100 text-red-800", cancelado: "bg-slate-200 text-slate-700",
}[s] || "bg-slate-100 text-slate-700");

// Bloco de resumo compacto (delta 2026-09-16, referência visual do PDF de
// relatório) — rótulo em caixa alta com peso forte + valor em destaque,
// mesma linguagem de hierarquia já aplicada aos cards de Fichas/
// Faturamento. Só apresentação: os totais continuam vindo exatamente dos
// mesmos cálculos/dados já existentes (nenhuma métrica nova).
function StatChip({ label, value, tone = "slate" }) {
  const toneCls = { slate: "text-slate-900", emerald: "text-emerald-700", red: "text-red-700", primary: "text-primary" }[tone] || "text-slate-900";
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-base font-heading font-bold mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
}

function DataTable({ columns, rows, empty = "Sem dados" }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-slate-600">
          <tr>{columns.map(c => <th key={c.key} className="py-3 px-3 font-medium">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
              {columns.map(c => <td key={c.key} className="py-2.5 px-3">{c.render ? c.render(r) : (r[c.key] ?? "")}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length} className="py-8 text-center text-slate-400">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// Item "conteúdo": card único por relatório com título/descrição, resumo de
// período/filtros aplicados, contagem real de registros, botão de PDF e
// tabela paginada — mesmo componente reaproveitado pelos 5 relatórios para
// nunca ter 5 implementações visuais diferentes da mesma estrutura.
function ReportCard({ icon: Icon, title, description, filterSummary, columns, rows, footer, onExportPdf, exporting, testId }) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [rows]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageAtual = Math.min(page, totalPages);
  const pageItems = rows.slice((pageAtual - 1) * PAGE_SIZE, pageAtual * PAGE_SIZE);

  return (
    <Card className="p-0 overflow-hidden rounded-2xl shadow-sm">
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-blue-100 p-2.5 text-blue-700 shrink-0"><Icon size={18} /></div>
            <div>
              <div className="font-heading font-bold text-[17px] text-slate-900 tracking-tight">{title}</div>
              {description && <div className="text-xs text-slate-500 mt-0.5">{description}</div>}
            </div>
          </div>
          <Button variant="outline" onClick={onExportPdf} disabled={exporting} data-testid={`${testId}-export-pdf`}>
            <FileDown size={16} /> {exporting ? "Gerando..." : "Exportar PDF"}
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span>{filterSummary}</span>
          <Badge className="rounded-full bg-slate-100 text-slate-700 font-medium" data-testid={`${testId}-count`}>
            {rows.length} registro{rows.length === 1 ? "" : "s"}
          </Badge>
        </div>
      </div>
      <DataTable columns={columns} rows={pageItems} />
      {footer}
      {rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
          <span data-testid={`${testId}-page-info`}>Página {pageAtual} de {totalPages} — {rows.length} registro{rows.length === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={pageAtual <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid={`${testId}-page-prev`}>
              <ChevronLeft size={16} />
            </Button>
            <Button size="sm" variant="outline" disabled={pageAtual >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid={`${testId}-page-next`}>
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Relatorios() {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ cliente_id: "", analista_id: "", start: "", end: "", status: "" });
  const [services, setServices] = useState([]);
  const [hours, setHours] = useState([]);
  const [invoicing, setInvoicing] = useState([]);
  const [boletos, setBoletos] = useState([]);
  const [prod, setProd] = useState([]);
  const [exportingPdf, setExportingPdf] = useState("");

  useEffect(() => {
    (async () => {
      const [c, u] = await Promise.all([api.get("/clients"), api.get("/users").catch(() => ({ data: [] }))]);
      setClients(c.data); setUsers(u.data);
    })();
  }, []);

  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const loadServices = async () => setServices((await api.get("/reports/services", { params })).data);
  const loadHours = async () => setHours((await api.get("/reports/hours", { params })).data);
  const loadInvoicing = async () => setInvoicing((await api.get("/reports/invoicing", { params })).data);
  const loadBoletos = async () => setBoletos((await api.get("/reports/boletos", { params })).data);
  const loadProd = async () => setProd((await api.get("/reports/productivity", { params })).data);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadServices(); loadHours(); loadInvoicing(); loadBoletos(); loadProd(); }, []);

  // "Limpar filtros" (delta 2026-09-17) — volta ao estado padrão (Cliente/
  // Analista/De/Até todos vazios) e recarrega os 5 relatórios sem nenhum
  // filtro aplicado; nunca depende do `f`/`params` já obsoletos do render
  // anterior (mesmo cuidado de closure já documentado em Fichas.jsx).
  const limparFiltros = async () => {
    setF({ cliente_id: "", analista_id: "", start: "", end: "", status: "" });
    const [s, h, i, b, p] = await Promise.all([
      api.get("/reports/services"), api.get("/reports/hours"), api.get("/reports/invoicing"),
      api.get("/reports/boletos"), api.get("/reports/productivity"),
    ]);
    setServices(s.data); setHours(h.data); setInvoicing(i.data); setBoletos(b.data); setProd(p.data);
  };

  const clientName = (id) => clients.find(c => c.id === id)?.razao_social || "";
  const analystName = (id) => users.find(u => u.id === id)?.name || "";

  // Item "informação resumida do período/filtros" — mesmo texto que o
  // backend usa no cabeçalho do PDF (nunca duas fontes de verdade
  // divergentes sobre o que "Todos" significa).
  const filterSummary = useMemo(() => {
    const periodo = (f.start || f.end) ? `Período: ${f.start ? fmtDate(f.start) : "—"} a ${f.end ? fmtDate(f.end) : "—"}` : "Período: Todos";
    const cliente = f.cliente_id ? `Cliente: ${clientName(f.cliente_id)}` : "Cliente: Todos";
    const analista = f.analista_id ? `Analista: ${analystName(f.analista_id)}` : "Analista: Todos";
    return `${periodo} · ${cliente} · ${analista}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, clients, users]);

  const totalFat = invoicing.reduce((a, i) => a + (i.valor_total || 0), 0);
  const totalHFat = invoicing.reduce((a, i) => a + (i.horas_total || 0), 0);
  const totBemit = boletos.reduce((a, b) => a + (b.valor || 0), 0);
  const totBpago = boletos.filter(b => b.status === "pago").reduce((a, b) => a + (b.valor || 0), 0);
  const totBaberto = boletos.filter(b => b.status === "em_aberto").reduce((a, b) => a + (b.valor || 0), 0);
  const totBvenc = boletos.filter(b => b.status === "vencido").reduce((a, b) => a + (b.valor || 0), 0);

  const totHR = hours.reduce((a, r) => a + (r.horas_realizadas || 0), 0);
  const totHA = hours.reduce((a, r) => a + (r.horas_abonadas || 0), 0);
  const totHF = hours.reduce((a, r) => a + (r.horas_faturadas || 0), 0);

  // Item 3 — PDF real gerado no backend (GET /reports/export/{report}/pdf),
  // nunca screenshot/print do navegador. Envia os MESMOS filtros já
  // aplicados na tela (params, idêntico ao que os loadX já usam) — o PDF
  // nunca traz registro fora do que está sendo exibido.
  const doExportPdf = async (report) => {
    setExportingPdf(report);
    try {
      const token = localStorage.getItem("token");
      const qs = new URLSearchParams(params).toString();
      const r = await fetch(`${API_BASE}/reports/export/${report}/pdf${qs ? `?${qs}` : ""}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error("Falha ao gerar PDF");
      const b = await r.blob();
      const cd = r.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `relatorio-${report}.pdf`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Não foi possível gerar o PDF do relatório.");
    } finally {
      setExportingPdf("");
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-bold">Relatórios</h1>
        <p className="text-slate-500 text-sm">Consulte, filtre e exporte os relatórios operacionais.</p>
      </div>

      {/* Filtros — card único horizontal (item "Filtros"), mesmo conjunto
          de sempre (Cliente/Analista/De/Até/Aplicar filtros), nenhum
          filtro novo criado. */}
      <Card className="p-4 rounded-2xl shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <Label className="text-xs text-slate-500">Cliente</Label>
            <Select value={f.cliente_id || "all"} onValueChange={v => setF({ ...f, cliente_id: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="rel-cliente"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-500">Analista</Label>
            <Select value={f.analista_id || "all"} onValueChange={v => setF({ ...f, analista_id: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="rel-analista"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{users.filter(u => u.role === "analista").map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label className="text-xs text-slate-500">De</Label><Input type="date" value={f.start} onChange={e => setF({ ...f, start: e.target.value })} data-testid="rel-start" /></div>
          <div className="md:col-span-2"><Label className="text-xs text-slate-500">Até</Label><Input type="date" value={f.end} onChange={e => setF({ ...f, end: e.target.value })} data-testid="rel-end" /></div>
          <div className="md:col-span-2">
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => { loadServices(); loadHours(); loadInvoicing(); loadBoletos(); loadProd(); }} data-testid="rel-aplicar">
              <Filter size={16} /> Filtrar
            </Button>
          </div>
          <div className="md:col-span-1">
            <Button variant="outline" className="w-full" onClick={limparFiltros} data-testid="rel-limpar-filtros" title="Limpar filtros">
              <X size={16} />
            </Button>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="services">
        <TabsList className="bg-white border border-slate-200 h-auto flex-wrap p-1">
          <TabsTrigger value="services" data-testid="tab-services" className="gap-1.5 data-[state=active]:text-primary data-[state=active]:font-semibold">
            <ClipboardList size={14} /> Serviços
          </TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours" className="gap-1.5 data-[state=active]:text-primary data-[state=active]:font-semibold">
            <Clock size={14} /> Horas
          </TabsTrigger>
          <TabsTrigger value="invoicing" data-testid="tab-invoicing" className="gap-1.5 data-[state=active]:text-primary data-[state=active]:font-semibold">
            <Receipt size={14} /> Faturamento
          </TabsTrigger>
          <TabsTrigger value="boletos" data-testid="tab-boletos" className="gap-1.5 data-[state=active]:text-primary data-[state=active]:font-semibold">
            <Barcode size={14} /> Boletos
          </TabsTrigger>
          <TabsTrigger value="productivity" data-testid="tab-productivity" className="gap-1.5 data-[state=active]:text-primary data-[state=active]:font-semibold">
            <TrendingUp size={14} /> Produtividade
          </TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="mt-4">
          <ReportCard
            icon={ClipboardList} title="Relatório de Serviços"
            description="Fichas de atendimento registradas, com cliente, analista, serviço e status."
            filterSummary={filterSummary} rows={services} exporting={exportingPdf === "services"}
            onExportPdf={() => doExportPdf("services")} testId="rel-services"
            columns={[
              { key: "numero", label: "Ficha", render: r => "#" + r.numero },
              { key: "cliente_id", label: "Cliente", render: r => clientName(r.cliente_id) },
              { key: "data_servico", label: "Data", render: r => fmtDate(r.data_servico) },
              { key: "analista_id", label: "Analista", render: r => analystName(r.analista_id) },
              { key: "tipo_servico", label: "Serviço" },
              { key: "horas_realizadas", label: "Horas", render: r => fmtHours(r.horas_realizadas) },
              { key: "status", label: "Status", render: r => <Badge className={`rounded-full ${statusColor(r.status)}`}>{statusPt(r.status)}</Badge> },
            ]}
          />
        </TabsContent>

        <TabsContent value="hours" className="mt-4">
          <ReportCard
            icon={Clock} title="Relatório de Horas"
            description="Horas realizadas, abonadas e faturadas por atendimento."
            filterSummary={filterSummary} rows={hours} exporting={exportingPdf === "hours"}
            onExportPdf={() => doExportPdf("hours")} testId="rel-hours"
            columns={[
              { key: "analista", label: "Analista" },
              { key: "cliente", label: "Cliente" },
              { key: "horas_realizadas", label: "Realizadas", render: r => fmtHours(r.horas_realizadas) },
              { key: "horas_abonadas", label: "Abonadas", render: r => fmtHours(r.horas_abonadas) },
              { key: "horas_faturadas", label: "Faturadas", render: r => fmtHours(r.horas_faturadas) },
            ]}
            footer={hours.length > 0 && (
              <div className="p-4 bg-slate-50 border-t border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatChip label="Total realizadas" value={fmtHours(totHR)} />
                <StatChip label="Total abonadas" value={fmtHours(totHA)} />
                <StatChip label="Total faturadas" value={fmtHours(totHF)} tone="primary" />
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="invoicing" className="mt-4">
          <ReportCard
            icon={Receipt} title="Relatório de Faturamento"
            description="Faturamentos gerados no período, com horas e valor."
            filterSummary={filterSummary} rows={invoicing} exporting={exportingPdf === "invoicing"}
            onExportPdf={() => doExportPdf("invoicing")} testId="rel-invoicing"
            columns={[
              { key: "numero", label: "Nº" },
              { key: "cliente_nome", label: "Cliente" },
              { key: "created_at", label: "Data", render: r => fmtDate(r.created_at) },
              { key: "horas_total", label: "Horas", render: r => fmtHours(r.horas_total) },
              { key: "valor_total", label: "Valor", render: r => money(r.valor_total) },
              { key: "status", label: "Status", render: r => <Badge className={`rounded-full ${invoiceStatusColor(r.status)}`}>{invoiceStatusPt(r.status)}</Badge> },
            ]}
            footer={invoicing.length > 0 && (
              <div className="p-4 bg-slate-50 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                <StatChip label="Total horas" value={fmtHours(totalHFat)} />
                <StatChip label="Total faturado" value={money(totalFat)} tone="primary" />
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="boletos" className="mt-4">
          <ReportCard
            icon={Barcode} title="Relatório de Boletos"
            description="Boletos emitidos, com situação e data de pagamento."
            filterSummary={filterSummary} rows={boletos} exporting={exportingPdf === "boletos"}
            onExportPdf={() => doExportPdf("boletos")} testId="rel-boletos"
            columns={[
              { key: "numero_documento", label: "Doc." },
              { key: "cliente_nome", label: "Cliente" },
              { key: "emissao", label: "Emissão", render: r => fmtDate(r.emissao) },
              { key: "vencimento", label: "Vencimento", render: r => fmtDate(r.vencimento) },
              { key: "valor", label: "Valor", render: r => money(r.valor) },
              { key: "status", label: "Status", render: r => <Badge className={`rounded-full ${boletoStatusColor(r.status)}`}>{boletoStatusPt(r.status)}</Badge> },
              { key: "pago_em", label: "Pago em", render: r => r.pago_em ? fmtDate(r.pago_em) : "-" },
            ]}
            footer={boletos.length > 0 && (
              <div className="p-4 bg-slate-50 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatChip label="Emitido" value={money(totBemit)} />
                <StatChip label="Recebido" value={money(totBpago)} tone="emerald" />
                <StatChip label="Em aberto" value={money(totBaberto)} />
                <StatChip label="Vencido" value={money(totBvenc)} tone="red" />
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="productivity" className="mt-4">
          <ReportCard
            icon={TrendingUp} title="Relatório de Produtividade"
            description="Atendimentos, horas e valor faturado por analista."
            filterSummary={filterSummary} rows={prod} exporting={exportingPdf === "productivity"}
            onExportPdf={() => doExportPdf("productivity")} testId="rel-productivity"
            columns={[
              { key: "analista", label: "Analista" },
              { key: "atendimentos", label: "Atendimentos" },
              { key: "horas_realizadas", label: "H. realizadas", render: r => fmtHours(r.horas_realizadas) },
              { key: "horas_abonadas", label: "H. abonadas", render: r => fmtHours(r.horas_abonadas) },
              { key: "horas_faturadas", label: "H. faturadas", render: r => fmtHours(r.horas_faturadas) },
              { key: "valor_faturado", label: "Valor faturado", render: r => money(r.valor_faturado) },
              { key: "clientes", label: "Clientes" },
            ]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
